import { keyValueMirror } from 'store/storeFunctions';
import { setGuidURL } from './canvasActions';
import { clearMessages } from 'store/actions/appActions';
import constants from 'constants';

const {
  API_HOST,
  JOB_PHASES,
  JOB_STATUSES,
  JOB_POLLING,
  DOWNLOAD_RETRY,
  DEMO_STAGE_DURATIONS,
} = constants;

export const actions = keyValueMirror(
  'SET_JOB_PHASE',
  'SET_JOB',
  'SET_JOB_MODEL',
  'SET_JOB_GUID',
  'SET_POLL_STARTED_AT',
  'SET_POLL_FAILURES',
  'SET_DOWNLOAD_ATTEMPTS',
  'SET_DOWNLOAD_URL',
  'CLEAR_JOB',
);

export const setJobPhase = payload => ({
  type: actions.SET_JOB_PHASE,
  payload,
});

export const setJob = payload => ({
  type: actions.SET_JOB,
  payload,
});

export const setJobModel = payload => ({
  type: actions.SET_JOB_MODEL,
  payload,
});

export const setJobGuid = payload => ({
  type: actions.SET_JOB_GUID,
  payload,
});

export const setPollStartedAt = payload => ({
  type: actions.SET_POLL_STARTED_AT,
  payload,
});

export const setPollFailures = payload => ({
  type: actions.SET_POLL_FAILURES,
  payload,
});

export const setDownloadAttempts = payload => ({
  type: actions.SET_DOWNLOAD_ATTEMPTS,
  payload,
});

export const setDownloadURL = payload => ({
  type: actions.SET_DOWNLOAD_URL,
  payload,
});

export const clearJob = () => ({
  type: actions.CLEAR_JOB,
});

const jsonRequest = {
  method: 'GET',
  headers: { accept: 'application/json' },
};

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

// Both endpoints are unauthenticated, matching checkModelEditable.
const getJSON = async url => {
  const res = await fetch(url, jsonRequest);
  if (!res.ok) return { status: res.status, data: null };

  const data = await res.json();
  return { status: res.status, data };
};

// A poll that resolves after the user has moved on to another model must not
// write its result over the newer job.
const isStaleJob = (getState, jobGuid) => getState().job.jobGuid !== jobGuid;

// The chain entry point: run only when the download itself 404s.
export const resolveModelJob = args => async dispatch => {
  const { guid, format } = args;

  // The status page owns the viewport from here — modals would stack on top.
  dispatch(clearMessages());
  dispatch(setJobPhase(JOB_PHASES.RESOLVING));
  dispatch(setPollFailures(0));
  dispatch(setDownloadAttempts(0));
  dispatch(setDownloadURL(null));

  let model;

  try {
    model = await getJSON(`${API_HOST}/models/${guid}`);
  } catch (error) {
    console.error(error);
    dispatch(setJobPhase(JOB_PHASES.UNAVAILABLE));
    return;
  }

  if (model.status === 404) {
    dispatch(setJobPhase(JOB_PHASES.NOT_FOUND));
    return;
  }

  if (!model.data) {
    dispatch(setJobPhase(JOB_PHASES.UNAVAILABLE));
    return;
  }

  dispatch(setJobModel({
    title: model.data.title,
    preview512URL: model.data.preview512URL,
  }));

  const jobGuid = model.data.job_guid;

  // The model record exists but claims no job, so there is nothing to wait for.
  if (!jobGuid) {
    dispatch(setJobPhase(JOB_PHASES.NOT_FOUND));
    return;
  }

  dispatch(setJobGuid(jobGuid));
  dispatch(setPollStartedAt(Date.now()));
  await dispatch(pollJob({ jobGuid, guid, format }));
};

// A single failed poll is not a state change — the API is allowed a hiccup.
const registerPollFailure = () => (dispatch, getState) => {
  const failures = (getState().job.pollFailures || 0) + 1;
  dispatch(setPollFailures(failures));

  if (failures >= JOB_POLLING.MAX_FAILURES) {
    dispatch(setJobPhase(JOB_PHASES.UNAVAILABLE));
  }
};

const applyJobStatus = args => (dispatch, getState) => {
  const { status, guid, format } = args;
  const phase = getState().job.phase;

  if (status === JOB_STATUSES.FAILED) {
    dispatch(setJobPhase(JOB_PHASES.FAILED));
    return;
  }

  if (status === JOB_STATUSES.COMPLETED) {
    const settled = phase === JOB_PHASES.COMPLETED || phase === JOB_PHASES.READY;
    if (settled) return;

    dispatch(setJobPhase(JOB_PHASES.COMPLETED));
    dispatch(requestDownloadURL({ guid, format }));
    return;
  }

  if (status === JOB_STATUSES.WAITING_FOR_FILE || status === JOB_STATUSES.QUEUED) {
    dispatch(setJobPhase(status));
    return;
  }

  // Forward compatible: an unknown stage keeps spinning rather than dead-ending.
  dispatch(setJobPhase(JOB_PHASES.PROCESSING));
};

export const pollJob = args => async (dispatch, getState) => {
  const { jobGuid, guid, format } = args;

  if (isStaleJob(getState, jobGuid)) return;

  let job;

  try {
    job = await getJSON(`${API_HOST}/jobs/${jobGuid}`);
  } catch (error) {
    console.error(error);
    if (!isStaleJob(getState, jobGuid)) dispatch(registerPollFailure());
    return;
  }

  if (isStaleJob(getState, jobGuid)) return;

  // A job we cannot read is a service problem, not a bad link — even on a 404.
  if (!job.data) {
    dispatch(registerPollFailure());
    return;
  }

  dispatch(setPollFailures(0));
  dispatch(setJob(job.data));
  dispatch(applyJobStatus({ status: job.data.status, guid, format }));
};

// COMPLETED does not guarantee the format is servable yet, so give it a budget.
export const requestDownloadURL = args => async (dispatch, getState) => {
  const { guid, format } = args;
  const { MAX_ATTEMPTS, DELAY } = DOWNLOAD_RETRY;
  const jobGuid = getState().job.jobGuid;
  const url = `${API_HOST}/models/${guid}/download/${format}`;

  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    if (isStaleJob(getState, jobGuid)) return;
    dispatch(setDownloadAttempts(attempt));

    try {
      const download = await getJSON(url);

      if (download.data) {
        if (isStaleJob(getState, jobGuid)) return;
        dispatch(setDownloadURL(download.data.url));
        dispatch(setJobPhase(JOB_PHASES.READY));
        return;
      }

      // Anything other than a 404 is not going to fix itself in 2 seconds.
      if (download.status !== 404) break;
    } catch (error) {
      console.error(error);
    }

    if (attempt < MAX_ATTEMPTS) await wait(DELAY);
  }

  if (isStaleJob(getState, jobGuid)) return;
  dispatch(setJobPhase(JOB_PHASES.UNAVAILABLE));
};

// Nothing loads until the user asks for it — no silent auto-load on COMPLETED.
export const openModel = () => (dispatch, getState) => {
  const downloadURL = getState().job.downloadURL;

  dispatch(clearJob());
  if (downloadURL) dispatch(setGuidURL(downloadURL));
};

/* ------------------------------------------------------------------ demo --- */

const DEMO_STAGES = [
  JOB_PHASES.WAITING_FOR_FILE,
  JOB_PHASES.QUEUED,
  JOB_PHASES.PROCESSING,
];

// Each run claims a token so a restart or a route change strands the old timers
// instead of letting them write over the new state.
let demoRun = 0;

/**
 * Scripted playback for constants.DEMO_GUID only.
 *
 * The demo model is already processed, so there is no real job to follow: the
 * phases are walked on a timer rather than polled, which is also why no jobGuid
 * is ever set — that is what keeps the poll scheduler out of this. The title
 * and thumbnail come from the live model record, and the last step resolves the
 * genuine download URL so the Open model button loads the real thing.
 */
export const runDemoJob = args => async (dispatch, getState) => {
  const { guid, format } = args;
  const runId = ++demoRun;

  const cancelled = () => (
    runId !== demoRun || getState().job.phase === JOB_PHASES.IDLE
  );

  dispatch(clearMessages());
  dispatch(setJobPhase(JOB_PHASES.RESOLVING));
  dispatch(setPollFailures(0));
  dispatch(setDownloadAttempts(0));
  dispatch(setDownloadURL(null));

  try {
    const model = await getJSON(`${API_HOST}/models/${guid}`);

    if (model.data) {
      dispatch(setJobModel({
        title: model.data.title,
        preview512URL: model.data.preview512URL,
      }));
    }
  } catch (error) {
    console.error(error);
  }

  if (cancelled()) return;

  // A synthetic job so the elapsed counter starts from the top of the demo.
  const createdAt = new Date().toISOString();

  for (const phase of DEMO_STAGES) {
    if (cancelled()) return;

    dispatch(setJob({ created_at: createdAt, status: phase }));
    dispatch(setJobPhase(phase));

    await wait(DEMO_STAGE_DURATIONS[phase]);
  }

  if (cancelled()) return;

  dispatch(setJob({ created_at: createdAt, status: JOB_STATUSES.COMPLETED }));
  dispatch(setJobPhase(JOB_PHASES.COMPLETED));
  dispatch(setDownloadAttempts(1));

  // Hold on the Preparing... spinner before arming the button.
  await wait(DEMO_STAGE_DURATIONS[JOB_PHASES.COMPLETED]);

  if (cancelled()) return;

  let download;

  try {
    download = await getJSON(`${API_HOST}/models/${guid}/download/${format}`);
  } catch (error) {
    console.error(error);
  }

  if (cancelled()) return;

  if (!download?.data) {
    dispatch(setJobPhase(JOB_PHASES.UNAVAILABLE));
    return;
  }

  dispatch(setDownloadURL(download.data.url));
  dispatch(setJobPhase(JOB_PHASES.READY));
};
